import { Navigate, useLocation } from "react-router-dom";
import type { Role } from "@salesboard/contracts";
import { useT } from "@/i18n";
import { LoadingState } from "@/components/ui/states";
import { useSession } from "@/features/auth/useSession";

/**
 * Route guards. Presentation only — every capability is enforced again by the API,
 * because hiding a screen is not security.
 */

export function RequireSession({ children }: { children: React.ReactNode }): React.JSX.Element {
  const t = useT();
  const session = useSession();
  const location = useLocation();

  if (session.isPending) {
    return (
      <div className="shell-width p-6">
        <LoadingState rows={4} label={t("session.checking")} />
      </div>
    );
  }

  // Remember where they were headed, so a deep link survives signing in.
  if (!session.data) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  return <>{children}</>;
}

export function RequireRole({
  role,
  children,
}: {
  role: Role;
  children: React.ReactNode;
}): React.JSX.Element {
  const session = useSession();

  if (session.isPending) return <LoadingState rows={3} />;
  if (!session.data) return <Navigate to="/login" replace />;
  // Sent home rather than shown a refusal: the entry point is absent for this role, so
  // arriving here at all means a typed URL or a stale link.
  if (session.data.role !== role) return <Navigate to="/" replace />;

  return <>{children}</>;
}
